import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import Modal from './Modal';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoData } from './EmptyState';
import { formatDate } from '../utils/dateFormatter';
import './ScheduledReports.css';

interface ReportConfig {
  id?: number;
  name: string;
  type: 'stock' | 'sales' | 'work_orders' | 'purchase_orders' | 'dashboard';
  format: 'pdf' | 'csv';
  schedule: 'daily' | 'weekly' | 'monthly' | 'none';
  scheduleTime?: string;
  scheduleDay?: number;
  recipients?: string[];
  filters?: Record<string, any>;
  isActive: boolean;
  created_at?: string;
  updated_at?: string;
}

interface GeneratedReport {
  name: string;
  size: number;
  created: string;
  modified: string;
}

export default function ScheduledReports() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [reports, setReports] = useState<ReportConfig[]>([]);
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ReportConfig | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'generated'>('config');

  const [formData, setFormData] = useState<ReportConfig>({
    name: '',
    type: 'stock',
    format: 'pdf',
    schedule: 'none',
    scheduleTime: '09:00',
    scheduleDay: undefined,
    recipients: [],
    filters: {},
    isActive: true,
  });

  useEffect(() => {
    loadReports();
    loadGeneratedReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports');
      setReports(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const loadGeneratedReports = async () => {
    try {
      const response = await api.get('/reports/generated');
      setGeneratedReports(response.data);
    } catch (err: any) {
      console.error('Error al cargar reportes generados:', err);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingReport(null);
    setFormData({
      name: '',
      type: 'stock',
      format: 'pdf',
      schedule: 'none',
      scheduleTime: '09:00',
      scheduleDay: undefined,
      recipients: [],
      filters: {},
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (report: ReportConfig) => {
    setEditingReport(report);
    setFormData(report);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingReport?.id) {
        await api.put(`/reports/${editingReport.id}`, formData);
        showSuccess('Reporte actualizado correctamente');
      } else {
        await api.post('/reports', formData);
        showSuccess('Reporte creado correctamente');
      }
      setIsModalOpen(false);
      loadReports();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al guardar reporte');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este reporte?')) {
      return;
    }

    try {
      await api.delete(`/reports/${id}`);
      showSuccess('Reporte eliminado correctamente');
      loadReports();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar reporte');
    }
  };

  const handleGenerate = async (id: number) => {
    try {
      const response = await api.get(`/reports/${id}/generate`, {
        responseType: 'blob',
      });

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${id}_${new Date().getTime()}.${response.headers['content-type']?.includes('pdf') ? 'pdf' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('Reporte generado y descargado correctamente');
      loadGeneratedReports();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al generar reporte');
    }
  };

  const handleDownloadGenerated = async (fileName: string) => {
    try {
      const response = await api.get(`/reports/download/${fileName}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar reporte');
    }
  };

  const getScheduleLabel = (report: ReportConfig) => {
    if (report.schedule === 'none') return 'No programado';
    if (report.schedule === 'daily') return `Diario a las ${report.scheduleTime || '09:00'}`;
    if (report.schedule === 'weekly') {
      const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      return `Semanal: ${days[report.scheduleDay || 1]} a las ${report.scheduleTime || '09:00'}`;
    }
    if (report.schedule === 'monthly') {
      return `Mensual: día ${report.scheduleDay || 1} a las ${report.scheduleTime || '09:00'}`;
    }
    return report.schedule;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      stock: 'Stock',
      sales: 'Ventas',
      work_orders: 'Órdenes de Fabricación',
      purchase_orders: 'Pedidos de Compra',
      dashboard: 'Dashboard',
    };
    return labels[type] || type;
  };

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Reportes Programados</h2>
        <div className="flex-gap">
          <div className="tab-buttons">
            <button
              className={`btn btn-sm ${activeTab === 'config' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('config')}
              title="Ver reportes programados que puedes crear y editar"
            >
              Reportes Programados
            </button>
            <button
              className={`btn btn-sm ${activeTab === 'generated' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('generated')}
              title="Ver reportes que ya han sido generados"
            >
              Reportes Generados
            </button>
          </div>
          {canEdit('dashboard') && (
            <button className="btn btn-primary" onClick={handleOpenCreateModal}>
              + Nuevo Reporte
            </button>
          )}
        </div>
      </div>

      {activeTab === 'config' ? (
        <>
          {loading ? (
            <SkeletonTable rows={5} columns={6} />
          ) : (
            <>
              {reports.length === 0 ? (
                <EmptyStateNoData />
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Tipo</th>
                        <th>Formato</th>
                        <th>Programación</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((report) => (
                        <tr key={report.id}>
                          <td>
                            <strong>{report.name}</strong>
                          </td>
                          <td>{getTypeLabel(report.type)}</td>
                          <td>
                            <span className="badge badge-info">{report.format.toUpperCase()}</span>
                          </td>
                          <td>{getScheduleLabel(report)}</td>
                          <td>
                            <span
                              className={`badge ${report.isActive ? 'badge-success' : 'badge-secondary'}`}
                            >
                              {report.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleGenerate(report.id!)}
                                title="Generar ahora"
                              >
                                📄
                              </button>
                              {canEdit('dashboard') && (
                                <>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleOpenEditModal(report)}
                                    title="Editar"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(report.id!)}
                                    title="Eliminar"
                                  >
                                    🗑️
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tamaño</th>
                <th>Fecha Creación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {generatedReports.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    No hay reportes generados
                  </td>
                </tr>
              ) : (
                generatedReports.map((report) => (
                  <tr key={report.name}>
                    <td>{report.name}</td>
                    <td>{(report.size / 1024).toFixed(2)} KB</td>
                    <td>{formatDate(report.created)}</td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleDownloadGenerated(report.name)}
                        title="Descargar"
                      >
                        📥 Descargar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Crear/Editar Reporte */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingReport ? 'Editar Reporte' : 'Nuevo Reporte'}
        size="large"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              data-modal-default="true"
            >
              {editingReport ? 'Actualizar' : 'Crear'}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="input-group">
            <label className="input-label">Nombre del Reporte</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Reporte Diario de Stock"
            />
          </div>

          <div className="input-group">
            <label className="input-label">Tipo de Reporte</label>
            <select
              className="input"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as ReportConfig['type'] })}
            >
              <option value="stock">Stock</option>
              <option value="sales">Ventas</option>
              <option value="work_orders">Órdenes de Fabricación</option>
              <option value="dashboard">Dashboard</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Formato</label>
            <select
              className="input"
              value={formData.format}
              onChange={(e) => setFormData({ ...formData, format: e.target.value as ReportConfig['format'] })}
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Programación</label>
            <select
              className="input"
              value={formData.schedule}
              onChange={(e) => setFormData({ ...formData, schedule: e.target.value as ReportConfig['schedule'] })}
            >
              <option value="none">No programado</option>
              <option value="daily">Diario</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
            </select>
          </div>

          {formData.schedule !== 'none' && (
            <>
              <div className="input-group">
                <label className="input-label">Hora</label>
                <input
                  type="time"
                  className="input"
                  value={formData.scheduleTime || '09:00'}
                  onChange={(e) => setFormData({ ...formData, scheduleTime: e.target.value })}
                />
              </div>

              {formData.schedule === 'weekly' && (
                <div className="input-group">
                  <label className="input-label">Día de la Semana</label>
                  <select
                    className="input"
                    value={formData.scheduleDay || 1}
                    onChange={(e) => setFormData({ ...formData, scheduleDay: parseInt(e.target.value) })}
                  >
                    <option value={0}>Domingo</option>
                    <option value={1}>Lunes</option>
                    <option value={2}>Martes</option>
                    <option value={3}>Miércoles</option>
                    <option value={4}>Jueves</option>
                    <option value={5}>Viernes</option>
                    <option value={6}>Sábado</option>
                  </select>
                </div>
              )}

              {formData.schedule === 'monthly' && (
                <div className="input-group">
                  <label className="input-label">Día del Mes</label>
                  <input
                    type="number"
                    className="input"
                    min="1"
                    max="31"
                    value={formData.scheduleDay || 1}
                    onChange={(e) => setFormData({ ...formData, scheduleDay: parseInt(e.target.value) })}
                  />
                </div>
              )}
            </>
          )}

          <div className="input-group">
            <label className="input-label">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              {' '}Activo
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
}

